import '../styles/GlobalDesign.css';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : process.env.REACT_APP_API_URL || '/api';

// ─── Styles inline partagés ───────────────────────────────────────
const INPUT_STYLE = {
  padding: '7px 10px', background: 'rgba(255,255,255,0.07)',
  border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
  color: '#F1F5F9', fontSize: '0.88rem', width: '100%', boxSizing: 'border-box',
};
const BTN = (bg, color = '#fff') => ({
  padding: '7px 14px', background: bg, border: 'none', borderRadius: '8px',
  color, fontWeight: '600', cursor: 'pointer', fontSize: '0.82rem', whiteSpace: 'nowrap',
});
const CELL = (w, align = 'left') => ({
  padding: '12px 14px', color: '#94A3B8', fontSize: '0.83rem',
  fontWeight: '600', textAlign: align, width: w,
});
const DCELL = (align = 'left') => ({
  padding: '12px 14px', textAlign: align, verticalAlign: 'middle',
});

// ─── Composant modal de confirmation ──────────────────────────────
const ConfirmModal = ({ plan, onConfirm, onCancel, deleting }) => (
  <div style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <div style={{
      background: '#1E293B', border: '1px solid rgba(239,68,68,0.3)',
      borderRadius: '16px', padding: '32px', maxWidth: '420px', width: '90%',
      boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    }}>
      <div style={{ fontSize: '2rem', textAlign: 'center', marginBottom: '12px' }}>🗑️</div>
      <h3 style={{ color: '#F1F5F9', textAlign: 'center', marginBottom: '8px', fontSize: '1.1rem' }}>
        Supprimer ce plan ?
      </h3>
      <p style={{ color: '#94A3B8', textAlign: 'center', marginBottom: '28px', fontSize: '0.88rem' }}>
        Le plan <strong style={{ color: '#F87171' }}>{plan.name}</strong> sera désactivé.<br />
        Les abonnements existants ne seront pas affectés.
      </p>
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
        <button onClick={onCancel} disabled={deleting} style={BTN('rgba(255,255,255,0.08)', '#CBD5E1')}>
          Annuler
        </button>
        <button onClick={onConfirm} disabled={deleting} style={BTN('rgba(239,68,68,0.85)')}>
          {deleting ? '⏳ Suppression...' : '🗑️ Confirmer la suppression'}
        </button>
      </div>
    </div>
  </div>
);

// ─── Ligne de plan (lecture ou édition) ───────────────────────────
const PlanRow = ({ planKey, plan, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [form, setForm] = useState({});

  const startEdit = () => {
    setForm({
      name:          plan.name,
      duration:      plan.duration,
      price_normal:  plan.price_normal,
      price_reseller: plan.price_reseller,
    });
    setEditing(true);
  };

  const cancel = () => setEditing(false);

  const save = async () => {
    const pNorm = parseFloat(form.price_normal);
    const pRes  = parseFloat(form.price_reseller);
    const dDays = parseInt(form.duration, 10);
    if (!form.name?.trim()) return alert('Le nom est requis');
    if (!dDays || dDays <= 0) return alert('La durée doit être > 0 jours');
    if (!pNorm || pNorm <= 0) return alert('Prix normal invalide');
    if (!pRes  || pRes  <= 0) return alert('Prix revendeur invalide');
    if (pRes > pNorm) return alert('Le prix revendeur ne peut pas dépasser le prix normal');

    setSaving(true);
    try {
      await onSave(planKey, { name: form.name.trim(), duration: dDays, price_normal: pNorm, price_reseller: pRes });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const margin = plan.price_normal - plan.price_reseller;
  const marginPct = plan.price_normal > 0 ? Math.round((margin / plan.price_normal) * 100) : 0;

  if (editing) {
    return (
      <tr style={{ background: 'rgba(96,165,250,0.07)', borderBottom: '1px solid rgba(96,165,250,0.25)' }}>
        {/* Nom */}
        <td style={DCELL()}>
          <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            style={{ ...INPUT_STYLE, width: '160px' }} placeholder="Nom du plan" />
        </td>
        {/* Durée */}
        <td style={DCELL('center')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', justifyContent: 'center' }}>
            <input type="number" min="1" value={form.duration}
              onChange={e => setForm(p => ({ ...p, duration: e.target.value }))}
              style={{ ...INPUT_STYLE, width: '70px', textAlign: 'center' }} />
            <span style={{ color: '#64748B', fontSize: '0.78rem' }}>j</span>
          </div>
        </td>
        {/* Prix normal */}
        <td style={DCELL('right')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
            <input type="number" min="1" value={form.price_normal}
              onChange={e => setForm(p => ({ ...p, price_normal: e.target.value }))}
              style={{ ...INPUT_STYLE, width: '100px', textAlign: 'right' }} />
            <span style={{ color: '#64748B', fontSize: '0.75rem' }}>F</span>
          </div>
        </td>
        {/* Prix revendeur */}
        <td style={DCELL('right')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
            <input type="number" min="1" value={form.price_reseller}
              onChange={e => setForm(p => ({ ...p, price_reseller: e.target.value }))}
              style={{ ...INPUT_STYLE, width: '100px', textAlign: 'right' }} />
            <span style={{ color: '#64748B', fontSize: '0.75rem' }}>F</span>
          </div>
        </td>
        {/* Marge — vide en édition */}
        <td style={DCELL('center')}>—</td>
        {/* Actions */}
        <td style={{ ...DCELL('center'), whiteSpace: 'nowrap' }}>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <button onClick={save} disabled={saving} style={BTN('rgba(34,197,94,0.85)')}>
              {saving ? '⏳' : '💾 Sauv.'}
            </button>
            <button onClick={cancel} disabled={saving} style={BTN('rgba(255,255,255,0.08)', '#94A3B8')}>
              ✕
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.025)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <td style={{ ...DCELL(), color: '#E2E8F0', fontWeight: '600' }}>{plan.name}</td>
      <td style={{ ...DCELL('center'), color: '#94A3B8' }}>
        <span style={{
          background: 'rgba(148,163,184,0.12)', padding: '3px 10px',
          borderRadius: '20px', fontSize: '0.82rem',
        }}>
          {plan.duration} jours
        </span>
      </td>
      <td style={{ ...DCELL('right'), color: '#60A5FA', fontWeight: '700' }}>
        {plan.price_normal.toLocaleString()} <span style={{ color: '#64748B', fontWeight: '400', fontSize: '0.78rem' }}>FCFA</span>
      </td>
      <td style={{ ...DCELL('right'), color: '#34D399', fontWeight: '700' }}>
        {plan.price_reseller.toLocaleString()} <span style={{ color: '#64748B', fontWeight: '400', fontSize: '0.78rem' }}>FCFA</span>
      </td>
      <td style={{ ...DCELL('center') }}>
        <span style={{
          background: marginPct >= 20 ? 'rgba(34,197,94,0.15)' : marginPct >= 10 ? 'rgba(234,179,8,0.15)' : 'rgba(239,68,68,0.15)',
          color: marginPct >= 20 ? '#22C55E' : marginPct >= 10 ? '#EAB308' : '#F87171',
          padding: '3px 8px', borderRadius: '6px', fontWeight: '700', fontSize: '0.82rem',
        }}>
          {marginPct}%
        </span>
      </td>
      <td style={{ ...DCELL('center'), whiteSpace: 'nowrap' }}>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
          <button onClick={startEdit} style={BTN('rgba(96,165,250,0.18)', '#60A5FA')}>
            ✏️ Modifier
          </button>
          <button onClick={() => onDelete(planKey)} style={BTN('rgba(239,68,68,0.15)', '#F87171')}>
            🗑️
          </button>
        </div>
      </td>
    </tr>
  );
};

// ─── Page principale ───────────────────────────────────────────────
const AdminPricing = () => {
  const [loading, setLoading]           = useState(true);
  const [message, setMessage]           = useState(null);
  const [plans, setPlans]               = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting]         = useState(false);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const loadPricing = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/admin/pricing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { pricing, plans: planMeta } = res.data;
      const merged = {};
      for (const key of Object.keys(planMeta)) {
        if (!planMeta[key].enabled) continue;
        merged[key] = {
          name:          planMeta[key].name,
          duration:      planMeta[key].duration,
          price_normal:  pricing.normal[key]   ?? 0,
          price_reseller: pricing.reseller[key] ?? 0,
        };
      }
      setPlans(merged);
    } catch (err) {
      showMessage('error', 'Impossible de charger : ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPricing(); }, [loadPricing]);

  const handleSavePlan = async (key, data) => {
    const token = localStorage.getItem('token');
    await axios.put(`${API_URL}/admin/pricing/plan/${key}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setPlans(prev => ({ ...prev, [key]: { ...prev[key], ...data } }));
    showMessage('success', `✅ Plan "${data.name}" mis à jour`);
  };

  const handleDeletePlan = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/pricing/plan/${confirmDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(prev => {
        const copy = { ...prev };
        delete copy[confirmDelete];
        return copy;
      });
      setConfirmDelete(null);
      showMessage('success', '✅ Plan supprimé avec succès');
    } catch (err) {
      showMessage('error', 'Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-section">
          <div className="loading-container"><div className="spinner" /><p>Chargement des plans...</p></div>
        </div>
      </AdminLayout>
    );
  }

  const planEntries = Object.entries(plans);

  return (
    <AdminLayout>
      {confirmDelete && (
        <ConfirmModal
          plan={plans[confirmDelete]}
          onConfirm={handleDeletePlan}
          onCancel={() => setConfirmDelete(null)}
          deleting={deleting}
        />
      )}

      <div className="admin-section">
        {/* En-tête */}
        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 className="section-title">💰 Gestion des Plans</h1>
            <p className="section-subtitle">Modifiez le nom, la durée et les prix — ou supprimez un plan</p>
          </div>
          <button onClick={loadPricing} style={BTN('rgba(255,255,255,0.07)', '#94A3B8')}>
            🔄 Actualiser
          </button>
        </div>

        {/* Alerte */}
        {message && (
          <div style={{
            marginBottom: '20px', padding: '13px 18px', borderRadius: '10px',
            background: message.type === 'success' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
            border: `1px solid ${message.type === 'success' ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)'}`,
            color: message.type === 'success' ? '#22C55E' : '#F87171', fontWeight: '600', fontSize: '0.9rem',
          }}>
            {message.text}
          </div>
        )}

        {/* Tableau des plans */}
        <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '20px 22px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: '700', color: '#F1F5F9', margin: 0 }}>
              📋 Plans d'abonnement ({planEntries.length})
            </h2>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th style={CELL('30%')}>Plan</th>
                  <th style={CELL('12%', 'center')}>Durée</th>
                  <th style={CELL('16%', 'right')}>Prix Client</th>
                  <th style={CELL('16%', 'right')}>Prix Revendeur</th>
                  <th style={CELL('10%', 'center')}>Marge</th>
                  <th style={CELL('16%', 'center')}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {planEntries.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: '#475569' }}>
                      Aucun plan actif. Rechargez la page.
                    </td>
                  </tr>
                ) : planEntries.map(([key, plan]) => (
                  <PlanRow
                    key={key}
                    planKey={key}
                    plan={plan}
                    onSave={handleSavePlan}
                    onDelete={key => setConfirmDelete(key)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Légende */}
        <div style={{ marginTop: '16px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          {[
            { color: '#22C55E', bg: 'rgba(34,197,94,0.15)',  label: 'Marge ≥ 20%' },
            { color: '#EAB308', bg: 'rgba(234,179,8,0.15)',  label: 'Marge 10–19%' },
            { color: '#F87171', bg: 'rgba(239,68,68,0.15)',  label: 'Marge < 10%' },
          ].map(({ color, bg, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ background: bg, color, padding: '2px 10px', borderRadius: '6px', fontWeight: '700', fontSize: '0.78rem' }}>
                x%
              </span>
              <span style={{ color: '#64748B', fontSize: '0.8rem' }}>{label}</span>
            </div>
          ))}
          <div style={{ marginLeft: 'auto', color: '#475569', fontSize: '0.8rem', display: 'flex', alignItems: 'center' }}>
            💡 Cliquez sur <strong style={{ color: '#60A5FA', margin: '0 4px' }}>✏️ Modifier</strong> pour éditer un plan
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminPricing;
